import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NewBuyerSellerType } from 'types/order';

interface PartyOrderData {
  exception_1099: boolean;
  is_foreign: boolean;
  declines_ssn_ein: boolean;
}

const useStyles = makeStyles((theme: any) => ({
  progressSlot: {
    height: 4,
    marginBottom: theme.spacing(1),
  },
  labelRow: {
    '@media (hover: none) and (pointer: coarse)': {
      minHeight: theme.spacing(5.5),
      alignItems: 'center',
    },
  },
  labelText: {
    color: theme.palette.primary.main,
  },
}));

interface FormReportingProps {
  handleUpdatePartyOrderData: (data: Partial<NewBuyerSellerType>) => void;
  initialValues: NewBuyerSellerType | undefined;
  isSaving?: boolean;
}

const FormReporting: FC<FormReportingProps> = ({
  handleUpdatePartyOrderData,
  initialValues,
  isSaving = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { control, reset } = useForm<PartyOrderData>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [reset, initialValues]);

  return (
    <div aria-busy={isSaving}>
      <Box className={classes.progressSlot}>{isSaving && <LinearProgress />}</Box>
      <FormControl component="fieldset" fullWidth>
        <FormGroup>
          <Controller
            name="exception_1099"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                className={classes.labelRow}
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    disabled={isSaving}
                    onChange={(e) => {
                      field.onChange(e);
                      handleUpdatePartyOrderData({ exception_1099: e.target.checked });
                    }}
                  />
                }
                label={
                  <Typography variant="body2" className={classes.labelText}>
                    {t('parties:exemptFrom1099')}
                  </Typography>
                }
              />
            )}
          />
          <Controller
            name="is_foreign"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                className={classes.labelRow}
                control={
                  <Checkbox
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleUpdatePartyOrderData({ is_foreign: e.target.checked });
                    }}
                    checked={field.value}
                    disabled={isSaving}
                  />
                }
                label={
                  <Typography variant="body2" className={classes.labelText}>
                    {t('parties:foreignPerson')}
                  </Typography>
                }
              />
            )}
          />
          <Controller
            name="declines_ssn_ein"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                className={classes.labelRow}
                control={
                  <Checkbox
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleUpdatePartyOrderData({ declines_ssn_ein: e.target.checked });
                    }}
                    checked={field.value}
                    disabled={isSaving}
                  />
                }
                label={
                  <Typography variant="body2" className={classes.labelText}>
                    {t('parties:declinesToProvideSSN')}
                  </Typography>
                }
              />
            )}
          />
        </FormGroup>
      </FormControl>
    </div>
  );
};

export default FormReporting;
